import { NEWS } from 'config/news';
import templateType from 'app/utilities/template-type';

// Templates that render the paginated news listing. Anything else on a `/news/*`
// URL is an article, which has no listing to prefetch.
export const NEWS_LISTING_TEMPLATES = ['news', 'news-archive', 'news-category', 'news-sub-category'];

// The listings that are not tied to a category of their own, and so list every
// article the site holds when nothing is ticked
const EVERY_CATEGORY_TEMPLATES = ['news', 'news-archive'];

export const CATEGORY_PARAM = 'category';
export const PAGE_PARAM = 'page';

// The listing endpoint takes a comma separated list of categories and returns
// the union of them, so the query string mirrors that shape exactly
const CATEGORY_SEPARATOR = ',';

// Past this the endpoint fails outright instead of returning nothing, so it is
// the ceiling for a page number no matter what the URL asks for
const MAX_PAGE = Math.floor(NEWS.RESULT_WINDOW / NEWS.FILTER_LIMIT);

/**
 * Resolves the template for a news entity. Level 1 and level 2 categories share
 * the same entity type but not the same template, so the level has to be
 * consulted on top of the type. Shared by the page component and the server
 * side prefetch so the two cannot drift apart.
 *
 * @param {string} type Entity type from the API
 * @param {string} slug Entity slug
 * @param {number} level Category level, level 2 categories only
 * @param {string} parent Parent category slug, level 2 categories only
 *
 * @return {string} templateType
 */
export const getNewsTemplateType = (type, slug, level, parent) => {
    const resolved = templateType(slug, type);

    if (resolved === 'news-category' && (level === 2 || Boolean(parent))) {
        return 'news-sub-category';
    }

    return resolved;
};

export const isNewsListingTemplate = (type) => NEWS_LISTING_TEMPLATES.includes(type);

/*
    The shapes the listing takes, which differ in more than styling. Only the grid
    accumulates: `?page=3` is the first three pages together to it, and the third
    slice on its own to the other two. Everything that reads a page number has to
    know which, because that is the difference between a URL costing one request
    and costing one per page it names.
*/
export const LAYOUT = {
    CAROUSEL: 'carousel',
    GRID: 'grid',
    PAGED: 'paged'
};

/*
    The landing page keeps the carousel, and a category page at either level the
    grid it adds to. The archive is the whole listing rather than a corner of it,
    deep enough that assembling a page out of every page before it would be
    hundreds of articles in one response, so it pages through slices instead.
*/
const TEMPLATE_LAYOUTS = {
    news: LAYOUT.CAROUSEL,
    'news-archive': LAYOUT.PAGED
};

export const getListingLayout = (template) => TEMPLATE_LAYOUTS[template] || LAYOUT.GRID;

// Both of the shapes that lay their articles out as cards in rows page at the
// size of a row, so their last row is only short at the end of the listing
export const getListingLimit = (layout) => (layout === LAYOUT.CAROUSEL ? NEWS.FILTER_LIMIT : NEWS.GRID_LIMIT);

// Pages either side of the current one that the numbered links reach directly.
// The first and the last are always among them, so the ends of the listing stay
// one click away however deep into it the reader is.
const PAGE_WINDOW = 2;

// Stands in for the pages left out between two that are not neighbours
export const PAGE_GAP = 'gap';

/**
 * The page numbers to offer as links, with gaps marked where the run gaps.
 *
 * @param {number} current Page being shown
 * @param {number} total Pages in the listing
 *
 * @return {array} page numbers and PAGE_GAP markers, in order
 */
export const getPageWindow = (current, total) => {
    if (!total || total < 2) {
        return [];
    }

    const at = Math.min(Math.max(current, 1), total);
    const shown = new Set([1, total]);

    for (let page = at - PAGE_WINDOW; page <= at + PAGE_WINDOW; page++) {
        if (page > 1 && page < total) {
            shown.add(page);
        }
    }

    const pages = [...shown].sort((one, two) => one - two);

    return pages.reduce((all, page, index) => {
        const previous = pages[index - 1];

        // A single page missing between two is written out rather than marked,
        // because standing in for one page with an ellipsis is longer than the
        // page number it hides
        if (index && page - previous === 2) {
            all.push(previous + 1);
        } else if (index && page - previous > 1) {
            all.push(PAGE_GAP);
        }

        all.push(page);

        return all;
    }, []);
};

/**
 * The pages one URL is made of. A grid page is every page up to it, which is
 * what makes "More stories" a URL the reader can share and come back to rather
 * than a button that only works from page one. The other two shapes serve a page
 * as the slice it names, so one URL is one request however deep it sits.
 *
 * How deep the grid goes is decided by how much has been published rather than
 * by a ceiling here: a page past the end of the listing is turned away with a
 * redirect before its run is assembled, so a hand written page number cannot
 * fan out into requests for pages that hold nothing.
 *
 * @param {string} layout
 * @param {number} page Page the URL asks for
 *
 * @return {array} page numbers, in order
 */
export const getListingPages = (layout, page) => {
    if (layout !== LAYOUT.GRID) {
        return [page];
    }

    return Array.from({ length: page }, (_, index) => index + 1);
};

/**
 * How many of the articles fetched the grid can show.
 *
 * Only whole rows are rendered, so that a load never leaves a half filled row in
 * the middle of the list with the next load's articles below it. The filter block
 * sits in the grid as though it were a card, which is why the first page of a
 * level 1 category shows one fewer article than the pages after it.
 *
 * The end of the listing is the exception: there is no row to wait for, so
 * whatever is left is shown however it falls.
 *
 * @param {number} fetched How many articles are held
 * @param {number} total Articles the listing has in all, if known yet
 * @param {number} leadingCells Cells taken before the first card, i.e. the filter
 *
 * @return {number} how many to render
 */
export const getVisibleCount = (fetched, total, leadingCells = 0) => {
    if (!fetched) {
        return 0;
    }

    if (total && fetched >= total) {
        return fetched;
    }

    const columns = NEWS.GRID_COLUMNS;
    const rows = Math.floor((fetched + leadingCells) / columns);

    return Math.max(0, rows * columns - leadingCells);
};

/**
 * Whether a category opens its listing with the feature row.
 *
 * Only an exact count of featured stories is laid out that way, so this is also
 * what decides whether those stories are left out of the listing below: a
 * category featuring some other number of them keeps every article in the
 * chronology, because none of them are being shown above it.
 *
 * @param {array} featuredNews Stories the CMS features on the category
 *
 * @return {boolean} true when the feature row is in use
 */
export const hasFeatureRow = (featuredNews) =>
    Array.isArray(featuredNews) && featuredNews.length === NEWS.FEATURE_ITEMS;

// Every listing lives under this, apart from the archive
const NEWS_ROOT = 'news';

// The archive is a page of its own rather than one under `/news`, and lists every
// category, so its listing can be named from the path alone
export const NEWS_ARCHIVE_SLUG = 'news-archive';

/**
 * @param {string} pathname
 *
 * @return {boolean} whether the path is the news archive
 */
export const isNewsArchivePath = (pathname) => {
    const segments = String(pathname || '').split('/').filter(Boolean);

    return segments.length === 1 && segments[0] === NEWS_ARCHIVE_SLUG;
};

/**
 * Splits a news URL into the category it is asking for, without needing the page
 * behind it to have been fetched.
 *
 * The slug alone does not say whether the URL is a category or an article, only
 * where to look one up, which is why callers have to match it against a
 * collection they already hold.
 *
 * @param {string} pathname
 *
 * @return {object|null} `{ parent, slug }`, both null on the landing page, or
 *                       null when the path is not under `/news` at all
 */
export const getListingPath = (pathname) => {
    const [root, ...rest] = String(pathname || '').split('/').filter(Boolean);

    if (root !== NEWS_ROOT) {
        return null;
    }

    return {
        parent: rest.length > 1 ? rest[0] : null,
        slug: rest.length ? rest[rest.length - 1] : null
    };
};

/**
 * @param {object|string} search Query object, or a `?a=b` search string
 * @param {string} key Parameter name
 *
 * @return {string|undefined} raw parameter value
 */
const readParam = (search, key) => {
    if (!search) {
        return undefined;
    }

    if (typeof search === 'string') {
        return new URLSearchParams(search).get(key) || undefined;
    }

    return search[key];
};

/**
 * `?page` is publicly controllable, so anything that is not a positive integer
 * falls back to the first page, and anything past the endpoint's own ceiling is
 * clamped rather than passed on as a request it answers with an error.
 *
 * @param {object|string} search Query object, or a `?a=b` search string
 *
 * @return {number} page number, between 1 and MAX_PAGE
 */
export const getPageNumber = (search) => {
    const page = parseInt(readParam(search, PAGE_PARAM), 10);

    if (!Number.isInteger(page) || page < 1) {
        return 1;
    }

    return Math.min(page, MAX_PAGE);
};

/**
 * @param {number} resultCount Total number of articles, across every page
 * @param {number} limit Articles per page
 *
 * @return {number} total number of pages
 */
export const getTotalPages = (resultCount, limit) => {
    if (!resultCount || !limit) {
        return 0;
    }

    return Math.ceil(resultCount / limit);
};

/**
 * @param {object|string} search Query object, or a `?a=b` search string
 *
 * @return {array} category slugs the listing is filtered by
 */
export const getCategoryFilters = (search) => {
    const raw = readParam(search, CATEGORY_PARAM);

    if (!raw) {
        return [];
    }

    return [...new Set(
        String(raw)
            .split(CATEGORY_SEPARATOR)
            .map((slug) => slug.trim())
            .filter(Boolean)
    )];
};

/**
 * Narrows a requested filter down to the categories that exist, in the order
 * the collection lists them.
 *
 * Dropping unknown slugs keeps a mistyped or stale query from producing a
 * canonical for a page that is not there. Normalising the order means
 * `?category=a,b` and `?category=b,a` cannot both exist as URLs for one listing.
 *
 * @param {array} categories Category collection holding `id` and `slug`
 * @param {array} slugs Requested category slugs
 *
 * @return {array} matching categories
 */
export const resolveCategories = (categories, slugs) => {
    if (!Array.isArray(categories) || !Array.isArray(slugs) || !slugs.length) {
        return [];
    }

    return categories.filter(({ slug }) => slugs.includes(slug));
};

/**
 * Adds a category to the filter, or takes it out when it is already there.
 *
 * @param {array} categories Category collection holding `id` and `slug`
 * @param {array} active Currently filtered slugs
 * @param {string} slug Category being ticked or unticked
 *
 * @return {array} the resulting slugs, in collection order
 */
export const toggleCategory = (categories, active = [], slug) => {
    const next = active.includes(slug) ?
        active.filter((current) => current !== slug) :
        [...active, slug];

    return resolveCategories(categories, next).map((category) => category.slug);
};

/**
 * Joins categories into the single value the listing endpoint expects. Takes
 * either ids or slugs, the endpoint accepts both.
 *
 * @param {array} categories Category ids or slugs
 *
 * @return {string|null} comma separated value, or null when there are none
 */
export const buildCategoryParam = (categories) => {
    const list = (categories || []).filter(Boolean);

    return list.length ? list.join(CATEGORY_SEPARATOR) : null;
};

/**
 * Builds the search string for a listing state, leaving out defaults so the
 * first unfiltered page stays on a clean path.
 *
 * @param {array} categories Category slugs, empty for no filter
 * @param {number} page Page number
 *
 * @return {string} search string, e.g. `?category=marine,zoo-births&page=2`
 */
export const buildListingSearch = (categories, page = 1) => {
    const params = new URLSearchParams();
    const value = buildCategoryParam(categories);

    if (value) {
        params.set(CATEGORY_PARAM, value);
    }

    if (page > 1) {
        params.set(PAGE_PARAM, String(page));
    }

    const search = params.toString();

    return search ? `?${search}` : '';
};

/**
 * Reshapes a category collection from a raw API response into the `{ id, slug }`
 * pairs `resolveCategories` needs. Used server side, where the response has not
 * been through the Immutable store and its selectors yet.
 *
 * @param {object} response Raw page API response
 * @param {string} key Response key holding the categories
 *
 * @return {array} categories
 */
const readCategories = (response, key) => {
    const collection = response && response[key] && response[key].data;

    if (!Array.isArray(collection)) {
        return [];
    }

    return collection
        .filter((category) => Boolean(category.attributes))
        .map((category) => ({ id: category.id, slug: category.attributes.slug }));
};

// The content block that puts the listing on a page which is not a news page of
// its own. One to a page: the listing is a single piece of store state.
export const FILTER_STORIES_BLOCK = 'filterstories';

/**
 * The listing a `filterstories` block on the page asks for, or null when there
 * is no such block.
 *
 * The block names its own categories, so the filter in the URL is resolved
 * against those rather than against the collections a news page carries, and
 * nothing ticked means every category the way it does on the landing page. It
 * renders as the grid, so a page of it is every page up to it.
 *
 * @param {object} attributes Page attributes from the API response
 * @param {object} query Parsed query string
 *
 * @return {object|null} `{ category, layout, limit, pages }`
 */
const getBlockListingRequest = (attributes, query) => {
    const blocks = attributes && attributes.blocks;
    const block = Array.isArray(blocks) ?
        blocks.find(({ type }) => type === FILTER_STORIES_BLOCK) :
        null;

    if (!block) {
        return null;
    }

    const categories = block.data && block.data.categories || [];
    const filtered = resolveCategories(categories, getCategoryFilters(query));

    return {
        category: buildCategoryParam(filtered.map(({ id }) => id)),
        layout: LAYOUT.GRID,
        limit: getListingLimit(LAYOUT.GRID),
        pages: getListingPages(LAYOUT.GRID, getPageNumber(query))
    };
};

/**
 * Works out which news listing to prefetch for a request, from the page API
 * response plus the query string. Returns null when the page has no listing,
 * which is how article pages avoid paying for a request they never render.
 *
 * The category filter is resolved against the category collections the page
 * response already carries, so no extra API call is needed to turn a slug from
 * the URL into the id the listing endpoint expects.
 *
 * @param {object} pageResponse Raw page API response
 * @param {object} query Parsed query string
 *
 * @return {object|null} `{ category, excludeFeatured, layout, limit, pages }`,
 *                       or null when there is no listing
 */
export const getNewsListingRequest = (pageResponse, query) => {
    const entity = pageResponse && pageResponse.data && pageResponse.data[0];

    if (!entity || !entity.attributes) {
        return null;
    }

    const { level, parent, slug } = entity.attributes;
    const template = getNewsTemplateType(entity.type, slug, level, parent);

    // A page that is not a news page can still carry the listing as a block, and
    // a news page's own listing comes first when it carries one as well
    if (!isNewsListingTemplate(template)) {
        return getBlockListingRequest(entity.attributes, query);
    }

    const collectionKey = template === 'news' ? 'news_categories' : 'subcategories';
    const filtered = resolveCategories(readCategories(pageResponse, collectionKey), getCategoryFilters(query));
    // Without a filter the listing shows the page's own category. The landing
    // page and the archive are not a category, which the endpoint reads as all
    // of them.
    const category = filtered.length ?
        buildCategoryParam(filtered.map(({ id }) => id)) :
        (EVERY_CATEGORY_TEMPLATES.includes(template) ? null : entity.id);
    const layout = getListingLayout(template);

    return {
        category: category,
        /*
            The stories shown above the listing are left out of it, so the page
            does not run the same two headlines twice. Only where the page shows
            them: the row is the level 2 category page's, and a filter narrowing
            the listing makes it a different listing to the one the row opens.
        */
        excludeFeatured: template === 'news-sub-category' &&
            !filtered.length &&
            hasFeatureRow(entity.attributes.featured_news),
        layout: layout,
        limit: getListingLimit(layout),
        pages: getListingPages(layout, getPageNumber(query))
    };
};
